import { Icon } from "@atoms";
import { PERMISSION_LEVEL } from '@constants/permissionLevel';
import { CLIENTS } from '@endpoints/clients';
import Spinner from '@legacy/core/components/Spinner';
import { AccessCheck } from '@particles/index';
import { ElementsConsumer, PaymentElement } from '@stripe/react-stripe-js';
import { faIcon } from "@utils/faIcon";
import replaceSlugs from '@utils/replaceSlugs';
import { Component, Fragment } from "react";
import ContactSearchOrCreateSelect, { contactToContactOption } from "../../clients/inputs/ContactSearchOrCreateSelect";
import ButtonGroup from "../../core/buttons/ButtonGroup";
import ButtonGroupRow from "../../core/buttons/ButtonGroupRow";
import UniversalButton from "../../core/buttons/UniversalButton";
import Banner from '../../core/components/Banner';
import BasicDisplayField from "../../core/fields/BasicDisplayField";
import BasicSelectField from "../../core/fields/BasicSelectField";
import CharField from "../../core/fields/CharField";
import DateField from "../../core/fields/DateField";
import PriceField from "../../core/fields/PriceField";
import SearchCreateSelectField from "../../core/fields/SearchCreateSelectField";
import SwitchField from "../../core/fields/SwitchField";
import TextField from "../../core/fields/TextField";
import { PaymentMethods, PAYMENT_METHOD_TYPE } from '../../core/utils/enums';
import { formatSavedBankLabel, formatSavedCardLabel, INTERNAL_CARD_PAYMENT_ELEMENT_OPTIONS, postPaymentJson } from '../../core/utils/stripePayments';
import { formatCurrencyForServiceCompany, getCurrencySymbolForServiceCompany, valueIsDefined } from "../../core/utils/utils";
import { calculateEstimateAmounts } from "../utils/utils";

const FORM_MODE_CAPTIONS = {
    ADD_PAYMENT: "Please provide the following information to add a payment to this estimate",
    EDIT_PAYMENT: "Use the fields below to edit this payment",
}

class EstimatePaymentForm extends Component {

    constructor(props) {
        super(props)

        this.state = {
            cardholder_name: props.estimate.service_location.external_client.name || "",
            recipientSearchInput: "",

            savedPaymentMethods: [],
            selectedPaymentMethodID: null,
            isLoadingPaymentMethods: false,
            useNewCard: false,
            hasLoadedPaymentMethods: false,
            lastLoadedPaymentMethodType: null,

            errors: {},
        }
    }

    componentDidMount() {
        // Return to the previous scroll
        document.querySelector(".main").scrollTo(0, this.props.returnScroll || 0)
    }

    async componentDidUpdate() {
        // Reload the saved methods whenever the active online type (card vs ACH) differs from what we last
        // loaded. We gate on the load-tracking state below (hasLoadedPaymentMethods / lastLoadedPaymentMethodType)
        // rather than diffing prevProps: the container mutates paymentData in place (see
        // EstimateDetailsContainer.updateFormData), so prevProps.payment is the very same object as
        // this.props.payment and can never reveal that the staff member switched payment_method or the
        // offline/online toggle. The needLoad check makes unrelated re-renders (memo/receipt edits, Stripe
        // finishing loading) a no-op on its own, so no prevProps short-circuit is needed.
        const isOnlineCardPayment = (this.props.payment.is_online_payment || false) && parseInt(this.props.payment.payment_method) === PaymentMethods.card;
        const isOnlineACHPayment = (this.props.payment.is_online_payment || false) && parseInt(this.props.payment.payment_method) === PaymentMethods.ach;

        let targetType = null;
        if (isOnlineCardPayment) {
            targetType = PAYMENT_METHOD_TYPE.card;
        } else if (isOnlineACHPayment) {
            targetType = PAYMENT_METHOD_TYPE.ach;
        }
        if (!targetType) {
            return;
        }

        const needLoad = !this.state.isLoadingPaymentMethods && (!this.state.hasLoadedPaymentMethods || this.state.lastLoadedPaymentMethodType !== targetType);
        if (!needLoad) {
            return;
        }

        const requestedIsOnlinePayment = this.props.payment.is_online_payment;
        const requestedPaymentMethod = this.props.payment.payment_method;

        this.setState({ isLoadingPaymentMethods: true });
        try {
            const endpoint = replaceSlugs(CLIENTS.LIST_PAYMENT_METHODS, {
                service_company_slug: window.CURRENT_USER.service_company.slug,
                client_id: this.props.estimate.service_location.external_client.id
            }) + `?type=${targetType}`;
            const response = await fetch(endpoint);
            const data = await response.json();

            if (!response.ok) {
                const error = new Error(data.error || 'Failed to load payment methods');
                error.status = response.status;
                throw error;
            }

            // Discard a stale response if the staff member switched the payment selection mid-request.
            const selectionChanged = this.props.payment.is_online_payment !== requestedIsOnlinePayment
                || this.props.payment.payment_method !== requestedPaymentMethod;
            if (selectionChanged) {
                this.setState({ isLoadingPaymentMethods: false });
                return;
            }

            const nextState = {
                savedPaymentMethods: data,
                isLoadingPaymentMethods: false,
                hasLoadedPaymentMethods: true,
                lastLoadedPaymentMethodType: targetType,
                selectedPaymentMethodID: data.length > 0 ? data[0].id : null,
            };

            if (targetType === PAYMENT_METHOD_TYPE.card) {
                // With no saved card on file, drop straight into the new-card entry flow.
                nextState.useNewCard = data.length === 0;
            } else {
                // Internal ACH has no new-account entry; the empty case shows a guidance banner instead.
                nextState.useNewCard = false;
            }

            this.setState(nextState);
        } catch (error) {
            // A 400 means the request can't be fulfilled given the client's current state -- in practice
            // "no Stripe customer yet" (the client has never paid online, so there are no saved methods to
            // list). That's expected, not a failure, so dismiss it silently and fall back to the empty state.
            // Anything else -- a 5xx, a 403/404, or a network/parse failure with no status -- is genuinely
            // unexpected and worth alarming the staff member with a toast.
            const isUnexpectedError = error.status !== 400;
            if (isUnexpectedError) {
                console.error("Failed to load payment methods", error);
                if (this.props.addToastToQueue) {
                    this.props.addToastToQueue({ type: 'error', size: 'md', title: "We couldn't load this client's saved payment methods. Please try again." });
                }
            }
            this.setState({
                isLoadingPaymentMethods: false,
                hasLoadedPaymentMethods: true,
                lastLoadedPaymentMethodType: targetType,
                useNewCard: targetType === PAYMENT_METHOD_TYPE.card,
            });
        }
    }

    chargePaymentMethod = async (stripePaymentMethodId) => {
        const { estimate } = this.props
        const isOnlineACHPayment = (this.props.payment.is_online_payment || false) && parseInt(this.props.payment.payment_method) === PaymentMethods.ach;
        // The off-session charge endpoint builds the PaymentIntent with exactly this method type.
        let paymentMethodTypeForCharge = PAYMENT_METHOD_TYPE.card;
        if (isOnlineACHPayment) {
            paymentMethodTypeForCharge = PAYMENT_METHOD_TYPE.ach;
        }

        const endpoint = DjangoUrls["estimates:api-estimates-payments-list"](window.MARKETPLACE_ENTITY_SLUG, estimate.id) + 'charge-payment-method/';
        try {
            await postPaymentJson(endpoint, {
                stripe_payment_method_id: stripePaymentMethodId,
                payment_method_type: paymentMethodTypeForCharge,
                receipt_email: this.props.selectedOnlinePaymentRecipient ? this.props.selectedOnlinePaymentRecipient.email : null,
            });
        } catch (error) {
            // The ACH and saved-card branches have no inline error slot, so surface the failure as a toast.
            // The new-card branch additionally renders errors.payment_data inline below its Payment Element.
            if (this.props.addToastToQueue) {
                this.props.addToastToQueue({ type: 'error', size: 'md', title: error.message });
            }
            this.setState({ errors: { ...this.state.errors, payment_data: error.message } });
            return;
        }
        location.assign(DjangoUrls["estimates:estimates-detail"](window.MARKETPLACE_ENTITY_SLUG, estimate.id))
    }

    submitPayment = async () => {
        const { estimate, stripe, elements } = this.props
        const { useNewCard } = this.state;
        const isOnlineCardPayment = (this.props.payment.is_online_payment || false) && parseInt(this.props.payment.payment_method) === PaymentMethods.card;
        const isOnlineACHPayment = (this.props.payment.is_online_payment || false) && parseInt(this.props.payment.payment_method) === PaymentMethods.ach;

        if (!isOnlineCardPayment && !isOnlineACHPayment) {
            return;
        }

        // Validate everything the off-session charge needs up front, flagging every gap at once before we
        // contact Stripe. The charge emails a receipt and stamps the payment method's billing details, so a
        // recipient with an email is always required (and a new card additionally needs a cardholder name).
        const validationErrors = {};
        if (!(this.props.selectedOnlinePaymentRecipient && this.props.selectedOnlinePaymentRecipient.email)) {
            validationErrors.receipt_email = 'Please choose a recipient to email the receipt to.';
        }
        if (isOnlineACHPayment && !this.state.selectedPaymentMethodID) {
            validationErrors.saved_payment_method = 'Please select a bank account.';
        }
        if (isOnlineCardPayment && useNewCard && this.state.cardholder_name === "") {
            validationErrors.cardholder_name = 'Please supply a cardholder name.';
        }
        if (Object.keys(validationErrors).length > 0) {
            this.setState({ errors: validationErrors });
            return;
        }
        this.setState({ errors: {} });

        if (isOnlineACHPayment) {
            await this.chargePaymentMethod(this.state.selectedPaymentMethodID);
            return;
        }

        if (useNewCard) {
            // Validate and collect the Payment Element, then create a PaymentMethod from it (deferred mode).
            const { error: submitError } = await elements.submit();
            if (submitError) {
                this.setState({ errors: { payment_data: submitError.message } });
                return;
            }
            const paymentMethodResult = await stripe.createPaymentMethod({
                elements,
                // The internal Payment Element opts out of name/email collection, so both are supplied here.
                params: { billing_details: { name: this.state.cardholder_name, email: this.props.selectedOnlinePaymentRecipient.email } },
            });
            if (paymentMethodResult.error) {
                this.setState({ errors: { payment_data: paymentMethodResult.error.message } });
                return;
            }

            // Store the new card for future use (best-effort); a failure here must never block the charge.
            try {
                const storeEndpoint = replaceSlugs(CLIENTS.SAVE_PAYMENT_METHOD, {
                    service_company_slug: window.CURRENT_USER.service_company.slug,
                    client_id: estimate.service_location.external_client.id
                });
                await postPaymentJson(storeEndpoint, { stripe_payment_method_id: paymentMethodResult.paymentMethod.id });
            } catch (error) {
                console.error("Failed to store payment method", error);
            }

            await this.chargePaymentMethod(paymentMethodResult.paymentMethod.id);
        } else {
            // Charging a saved card directly.
            await this.chargePaymentMethod(this.state.selectedPaymentMethodID);
        }
    }

    renderButtons = () => {
        const {
            mode,
            submitting,
            estimate,
            payment,
            errors,
            onFormDataChange,
            requestAction,
            switchToPrimaryForm,
            switchToSecondaryForm,
            paymentMethodOptions,
            updateOnlinePaymentRecipientSelection,
            selectedOnlinePaymentRecipient,
        } = this.props
        const { downPaymentAmountDue } = calculateEstimateAmounts(estimate)

        if (errors.unexpectedError) {
            return (
                <div className="data-panel__action-feedback">
                    <span className="text-invalid"><strong>An unexpected error occurred.</strong></span>
                </div>
            )
        }
        else {
            if (submitting) {
                return <Spinner centered={true} />
            }
            else {
                const isOnlineCardPayment = (payment.is_online_payment || false) && parseInt(payment.payment_method) === PaymentMethods.card;
                const isOnlineACHPayment = (payment.is_online_payment || false) && parseInt(payment.payment_method) === PaymentMethods.ach;
                if (isOnlineCardPayment || isOnlineACHPayment) {
                    return (
                        <ButtonGroup>
                            {!(isOnlineACHPayment && !this.state.isLoadingPaymentMethods && this.state.savedPaymentMethods.length === 0) && (
                                <ButtonGroupRow>
                                    <UniversalButton type="primary" text={`Charge ${formatCurrencyForServiceCompany(downPaymentAmountDue, estimate.service_company)} Now`} handler={event => this.submitPayment()} disabled={this.state.isLoadingPaymentMethods || (isOnlineACHPayment && !this.state.selectedPaymentMethodID)}/>
                                </ButtonGroupRow>
                            )}
                            <ButtonGroupRow>
                                <UniversalButton type="secondary" text="Cancel" handler={event => switchToPrimaryForm()} />
                            </ButtonGroupRow>
                        </ButtonGroup>
                    )
                }
                else {
                    if (mode.startsWith("ADD_")) {
                        return (
                            <ButtonGroup>
                                <ButtonGroupRow>
                                    <UniversalButton type="primary" text="Add" handler={event => requestAction(mode)} />
                                    <UniversalButton type="secondary" text="Cancel" handler={event => switchToPrimaryForm()} />
                                </ButtonGroupRow>
                            </ButtonGroup>
                        )
                    }
                    else {
                        return (
                            <ButtonGroup>
                                <ButtonGroupRow>
                                    <UniversalButton type="primary" text="Save" handler={event => requestAction(mode)} />
                                </ButtonGroupRow>
                                <ButtonGroupRow>
                                <AccessCheck permissions={{
                                    estimates_delete_permission: PERMISSION_LEVEL.FULL
                                }}>
                                    <UniversalButton type="danger" text="Delete" handler={event => requestAction("DELETE_" + mode.replace("ADD_", "").replace("EDIT_", ""))} />
                                </AccessCheck>
                                    <UniversalButton type="secondary" text="Cancel" handler={event => switchToPrimaryForm()} />
                                </ButtonGroupRow>
                            </ButtonGroup>
                        )
                    }
                }
            }
        }
    }

    renderOnlineCardFields = (downPaymentAmountDue) => {
        const {
            estimate,
            payment,
            errors,
            onFormDataChange,
            switchToSecondaryForm,
            updateOnlinePaymentRecipientSelection,
            selectedOnlinePaymentRecipient,
        } = this.props
        const { savedPaymentMethods, isLoadingPaymentMethods, useNewCard } = this.state

        if (isLoadingPaymentMethods) {
            return <Spinner centered={true} />
        }

        // Saved cards plus a "Use a new card" option.
        const paymentMethodDropdownOptions = [
            ...(savedPaymentMethods.map((method) => ({
                value: method.id,
                label: formatSavedCardLabel(method),
            }))),
            { value: 'new_card', label: 'Use a new card' },
        ];

        return (
            <>
                {savedPaymentMethods.length > 0 && (
                    <BasicSelectField
                        fieldName="saved_payment_method"
                        fieldLabel="Select Payment Method"
                        fieldValue={useNewCard ? 'new_card' : this.state.selectedPaymentMethodID}
                        fieldOnChange={(value) => {
                            const isNewCard = value === 'new_card';
                            this.setState({
                                useNewCard: isNewCard,
                                selectedPaymentMethodID: isNewCard ? null : value
                            });
                        }}
                        choices={paymentMethodDropdownOptions}
                        errors={this.state.errors}
                    />
                )}

                {useNewCard && (
                <>
                <div className="data-panel__form__field" id="div_id_card_data" aria-label="Card Information">
                    <label htmlFor="id_card_data" className="data-panel__form__field__label in-form">Card Information</label>
                    <div className="data-panel__form__field__input data-panel__form__field__input--stripe-elements">
                        <PaymentElement options={INTERNAL_CARD_PAYMENT_ELEMENT_OPTIONS}/>
                    </div>
                    {this.state.errors.payment_data && <div className="data-panel__form__field__errors" aria-label="Field Errors">{this.state.errors.payment_data}</div>}
                </div>
                <CharField
                    fieldName="cardholder_name"
                    fieldLabel="Name on Card"
                    fieldValue={this.state.cardholder_name || ""}
                    fieldOnChange={cardholder_name => this.setState({"cardholder_name": cardholder_name || ""})}
                    errors={this.state.errors}  /* Name is validated inside of this component */
                ></CharField>
                </>
                )}
                <SearchCreateSelectField
                    fieldName="receipt_email"
                    fieldLabel="To whom should the receipt be emailed?"
                    fieldValue={selectedOnlinePaymentRecipient ? selectedOnlinePaymentRecipient.email : null}
                    inputComponent={
                        <ContactSearchOrCreateSelect
                            disabled={window.CURRENT_USER?.permissions.clients_view_permission < PERMISSION_LEVEL.FULL}
                            fieldName="reporter"
                            restrictOptionsTo="email"
                            onSelectionChange={selectedOption => updateOnlinePaymentRecipientSelection(selectedOption != null ? selectedOption.object : null)}
                            onInputChange={(input, action) => {action.action === "input-change" && this.setState({recipientSearchInput: input})}}
                            defaultSelected={selectedOnlinePaymentRecipient ? contactToContactOption(selectedOnlinePaymentRecipient, "email") : null}
                            client={estimate.service_location.external_client}
                            serviceLocation={estimate.service_location}
                            showCreateButton={window.CURRENT_USER?.permissions.clients_edit_permission >= PERMISSION_LEVEL.FULL}
                            onCreateClick={event => switchToSecondaryForm("ADD_CONTACT", null, {name: this.state.recipientSearchInput, populationRef: "updateOnlinePaymentRecipientSelection"})}
                        ></ContactSearchOrCreateSelect>
                    }
                    showButton={selectedOnlinePaymentRecipient !== null && valueIsDefined(selectedOnlinePaymentRecipient.id) && !valueIsDefined(selectedOnlinePaymentRecipient.__isNonExistant__) && window.CURRENT_USER?.permissions.clients_edit_permission >= PERMISSION_LEVEL.FULL}
                    buttonLabel={<Fragment><Icon containerClassName="icon-inline" icon={faIcon({ name: "pen-to-square", style: "regular" })} />Edit Contact</Fragment>}
                    buttonAction={event => switchToSecondaryForm("EDIT_CONTACT", selectedOnlinePaymentRecipient, {populationRef: "updateOnlinePaymentRecipientSelection", dataRef: "selectedOnlinePaymentRecipient"})}
                    errors={this.state.errors}
                ></SearchCreateSelectField>
                <TextField
                    fieldName="payment_memo"
                    fieldLabel="Payment Memo"
                    fieldValue={payment.payment_memo || ""}
                    fieldOnChange={payment_memo => onFormDataChange("payment_memo", payment_memo || "")}
                    rows={3}
                    placeholder="Notes on this transaction. e.g. 'Payment 1 of 2' or 'Transfer from account #00000'."
                    optional={true}
                    errors={errors}
                ></TextField>
                <Banner type="info" text={<span>{window.STRIPE_PUBLISHABLE_KEY && window.STRIPE_PUBLISHABLE_KEY.includes("test") && <span className="stripe-test-signifier">Test Mode</span>} You're charging <strong>{formatCurrencyForServiceCompany(downPaymentAmountDue, estimate.service_company)} {estimate.service_company.currency_code}</strong> as a down payment on Estimate <strong>{estimate.custom_id ? estimate.custom_id : estimate.id}</strong></span>} />
            </>
        )
    }

    renderOnlineACHFields = (downPaymentAmountDue) => {
        const {
            estimate,
            payment,
            errors,
            onFormDataChange,
            switchToSecondaryForm,
            updateOnlinePaymentRecipientSelection,
            selectedOnlinePaymentRecipient,
        } = this.props
        const { savedPaymentMethods, isLoadingPaymentMethods } = this.state

        if (isLoadingPaymentMethods) {
            return <Spinner centered={true} />
        }

        if (savedPaymentMethods.length === 0) {
            return (
                <Banner
                    type="warning"
                    text={
                        <span>
                            This client has no bank accounts on file, so an ACH payment can&apos;t be collected here. To collect via ACH, either send this estimate to the client so they can pay it online, or add a bank account from the client&apos;s <a className="text-link text-link--inline" href={DjangoUrls["clients:clients-detail"](window.MARKETPLACE_ENTITY_SLUG, estimate.service_location.external_client.id)} target="_blank" rel="noopener">Payment Methods</a> page.
                        </span>
                    }
                />
            )
        }

        return (
            <>
                <BasicSelectField
                    fieldName="saved_payment_method"
                    fieldLabel="Bank Account"
                    fieldValue={this.state.selectedPaymentMethodID}
                    fieldOnChange={(value) => this.setState({ selectedPaymentMethodID: value })}
                    choices={savedPaymentMethods.map(method => ({
                        value: method.id,
                        label: formatSavedBankLabel(method)
                    }))}
                    errors={this.state.errors}
                />

                <SearchCreateSelectField
                    fieldName="receipt_email"
                    fieldLabel="To whom should the receipt be emailed?"
                    fieldValue={selectedOnlinePaymentRecipient ? selectedOnlinePaymentRecipient.email : null}
                    inputComponent={
                        <ContactSearchOrCreateSelect
                            disabled={window.CURRENT_USER?.permissions.clients_view_permission < PERMISSION_LEVEL.FULL}
                            fieldName="reporter"
                            restrictOptionsTo="email"
                            onSelectionChange={selectedOption => updateOnlinePaymentRecipientSelection(selectedOption != null ? selectedOption.object : null)}
                            onInputChange={(input, action) => {action.action === "input-change" && this.setState({recipientSearchInput: input})}}
                            defaultSelected={selectedOnlinePaymentRecipient ? contactToContactOption(selectedOnlinePaymentRecipient, "email") : null}
                            client={estimate.service_location.external_client}
                            serviceLocation={estimate.service_location}
                            showCreateButton={window.CURRENT_USER?.permissions.clients_edit_permission >= PERMISSION_LEVEL.FULL}
                            onCreateClick={event => switchToSecondaryForm("ADD_CONTACT", null, {name: this.state.recipientSearchInput, populationRef: "updateOnlinePaymentRecipientSelection"})}
                        ></ContactSearchOrCreateSelect>
                    }
                    showButton={selectedOnlinePaymentRecipient !== null && valueIsDefined(selectedOnlinePaymentRecipient.id) && !valueIsDefined(selectedOnlinePaymentRecipient.__isNonExistant__) && window.CURRENT_USER?.permissions.clients_edit_permission >= PERMISSION_LEVEL.FULL}
                    buttonLabel={<Fragment><Icon containerClassName="icon-inline" icon={faIcon({ name: "pen-to-square", style: "regular" })} />Edit Contact</Fragment>}
                    buttonAction={event => switchToSecondaryForm("EDIT_CONTACT", selectedOnlinePaymentRecipient, {populationRef: "updateOnlinePaymentRecipientSelection", dataRef: "selectedOnlinePaymentRecipient"})}
                    errors={this.state.errors}
                ></SearchCreateSelectField>
                <TextField
                    fieldName="payment_memo"
                    fieldLabel="Payment Memo"
                    fieldValue={payment.payment_memo || ""}
                    fieldOnChange={payment_memo => onFormDataChange("payment_memo", payment_memo || "")}
                    rows={3}
                    placeholder="Notes on this transaction. e.g. 'Payment 1 of 2' or 'Transfer from account #00000'."
                    optional={true}
                    errors={errors}
                ></TextField>
                <Banner type="info" text={<span>{window.STRIPE_PUBLISHABLE_KEY && window.STRIPE_PUBLISHABLE_KEY.includes("test") && <span className="stripe-test-signifier">Test Mode</span>} You're charging <strong>{formatCurrencyForServiceCompany(downPaymentAmountDue, estimate.service_company)} {estimate.service_company.currency_code}</strong> for Estimate <strong>{estimate.custom_id ? estimate.custom_id : estimate.id}</strong></span>} />
            </>
        )
    }

    renderOfflineFields = () => {
        const { estimate, payment, errors, onFormDataChange } = this.props
        return (
            <>
                <PriceField
                    fieldName="amount"
                    fieldLabel="Payment Amount"
                    fieldValue={payment.amount || ""}
                    fieldOnChange={amount => onFormDataChange("amount", amount || 0)}
                    currencySymbol={getCurrencySymbolForServiceCompany(estimate.service_company)}
                    errors={errors}
                ></PriceField>
                <DateField
                    fieldName="payment_date"
                    fieldLabel="Payment Date"
                    fieldValue={payment.payment_date || ""}
                    fieldOnChange={payment_date => onFormDataChange("payment_date", payment_date || "")}
                    errors={errors}
                ></DateField>
                <CharField
                    fieldName="reference_id"
                    fieldLabel="Reference ID"
                    fieldValue={payment.reference_id}
                    fieldOnChange={reference_id => onFormDataChange("reference_id", reference_id)}
                    optional={true}
                    errors={errors}
                ></CharField>
                <TextField
                    fieldName="payment_memo"
                    fieldLabel="Payment Memo"
                    fieldValue={payment.payment_memo || ""}
                    fieldOnChange={payment_memo => onFormDataChange("payment_memo", payment_memo || "")}
                    rows={3}
                    placeholder="Notes on this transaction. e.g. 'Payment 1 of 2' or 'Transfer from account #00000'."
                    optional={true}
                    errors={errors}
                ></TextField>
            </>
        )
    }

    renderPaymentFields = (downPaymentAmountDue) => {
        const { payment } = this.props
        const isOnlineCardPayment = (payment.is_online_payment || false) && parseInt(payment.payment_method) === PaymentMethods.card;
        const isOnlineACHPayment = (payment.is_online_payment || false) && parseInt(payment.payment_method) === PaymentMethods.ach;
        if (isOnlineCardPayment) {
            return this.renderOnlineCardFields(downPaymentAmountDue);
        }
        if (isOnlineACHPayment) {
            return this.renderOnlineACHFields(downPaymentAmountDue);
        }
        return this.renderOfflineFields();
    }

    render() {
        const {
            mode,
            estimate,
            payment,
            errors,
            onFormDataChange,
            paymentMethodOptions,
            stripe,
            elements,
        } = this.props
        const { downPaymentAmountDue } = calculateEstimateAmounts(estimate)

        return stripe && elements && (
            <div className="data-panel-container data-panel-container--with-margin">
                <div className="data-panel" aria-label="Estimate Payment Create/Update">
                    <div className="data-panel__form" aria-label="Estimate Payment Create/Update Form">
                        <p className="data-panel__form__caption">{FORM_MODE_CAPTIONS[mode]}</p>
                        {
                            mode !== "EDIT_PAYMENT" && (
                                <BasicDisplayField
                                    fieldName="down_payment_amount_due"
                                    fieldLabel="Down Payment Amount Due"
                                    fieldValue={formatCurrencyForServiceCompany(downPaymentAmountDue, estimate.service_company)}
                                ></BasicDisplayField>
                            )
                        }
                        <BasicSelectField
                            fieldName="payment_method"
                            fieldLabel="Payment Method"
                            fieldValue={payment.payment_method}
                            fieldOnChange={payment_method => onFormDataChange("payment_method", payment_method)}
                            choices={paymentMethodOptions}
                            errors={errors}
                        ></BasicSelectField>
                        {
                            valueIsDefined(payment.payment_method) && parseInt(payment.payment_method) === PaymentMethods.other && (
                                <CharField
                                    fieldName="payment_method_other_name"
                                    fieldLabel="Payment Method Name"
                                    fieldValue={payment.payment_method_other_name}
                                    fieldOnChange={payment_method_other_name => onFormDataChange("payment_method_other_name", payment_method_other_name)}
                                    optional={true}
                                    errors={errors}
                                ></CharField>
                            )
                        }
                        {
                            valueIsDefined(payment.payment_method) && (parseInt(payment.payment_method) === PaymentMethods.card || parseInt(payment.payment_method) === PaymentMethods.ach) && (
                                <SwitchField
                                    disabled={mode === "EDIT_PAYMENT" || !(estimate.service_company.accept_online_payments && estimate.service_company.online_payments_configured && estimate.accept_online_payments && downPaymentAmountDue.toFixed(2) > 0.50)}
                                    fieldName="is_online_payment"
                                    fieldLabel="Payment Type"
                                    fieldValue={payment.is_online_payment || false}
                                    fieldOnChange={is_online_payment => onFormDataChange("is_online_payment", is_online_payment)}
                                    checkedText="Online"
                                    uncheckedText="Offline"
                                    errors={errors}
                                ></SwitchField>
                            )
                        }
                        {this.renderPaymentFields(downPaymentAmountDue)}
                    </div>
                    {this.renderButtons()}
                </div>
            </div>
        )
    }
}

export default (props) => <ElementsConsumer>{({stripe, elements}) => <EstimatePaymentForm {...props} stripe={stripe} elements={elements} />}</ElementsConsumer>
